import { CommonModule } from '@angular/common';
import { Component, computed, effect, inject, signal } from '@angular/core';
import {
  FormControl,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import {
  CreatePreRegistrationDto,
  CreateMarketingConsentDto,
  PreRegistrationService,
  MarketingConsentService,
  EventService,
  EventDto,
} from '@millionshow/api-client';
import {
  ToastService,
  PageModalContainer,
  ModalService,
} from '@millionshow/client-libs';
import { of } from 'rxjs';
import { rxResource, toSignal } from '@angular/core/rxjs-interop';
import { Router } from '@angular/router';
import {
  AWARENESS_LABELS,
  AwarenessChannel,
  Region,
  REGION_LABELS,
} from '@millionshow/common';
import { RegistrationCompleteModal } from '../registration/modals/registration-complete.modal';
import { injectQueryParams } from 'ngxtension/inject-query-params';
import { PolicyModal } from '../../modals/policy/policy.modal';
import { RegistrationInfoComponent } from './components/registration-info.component';
import { RegistrationPriceInfoComponent } from './components/registration-price-info.component';
import { RegistrationActionButtonsComponent } from './components/registration-action-buttons.component';

@Component({
  selector: 'app-registration-form',
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    PageModalContainer,
    RegistrationInfoComponent,
    RegistrationPriceInfoComponent,
    RegistrationActionButtonsComponent,
  ],
  templateUrl: './registration-form.page.html',
})
export default class RegistrationFormPage {
  private readonly preRegistrationService = inject(PreRegistrationService);
  private readonly marketingConsentService = inject(MarketingConsentService);
  private readonly eventService = inject(EventService);
  private readonly toastService = inject(ToastService);
  private readonly router = inject(Router);
  private readonly modalService = inject(ModalService);

  readonly queryEventId = injectQueryParams<string>('eventId');

  allAgreed = signal<boolean>(false);
  isTermsAgreed = signal<boolean>(false);
  isPrivacyAgreed = signal<boolean>(false);

  regionOptions = Object.entries(REGION_LABELS).map(([key, label]) => ({
    value: key as Region,
    label,
  }));

  awarenessOptions = Object.entries(AWARENESS_LABELS).map(([key, label]) => ({
    value: key as AwarenessChannel,
    label,
  }));

  form = new FormGroup({
    eventId: new FormControl<string>('', {
      nonNullable: true,
      validators: [Validators.required],
    }),
    name: new FormControl<string>('', {
      nonNullable: true,
      validators: [Validators.required],
    }),
    contact: new FormControl<string>('', {
      nonNullable: true,
      validators: [Validators.required],
    }),
    interestedRegions: new FormControl<Region[]>([], {
      nonNullable: true,
    }),
    visitMotives: new FormControl<AwarenessChannel[]>([], {
      nonNullable: true,
    }),
  });

  readonly eventIdValueSignal = toSignal(
    this.form.controls.eventId.valueChanges
  );

  $events = rxResource({
    stream: () =>
      this.eventService.eventControllerFindAllV1({
        status: 'ONGOING',
        type: 'STARTFAIR' as const,
      }),
  });

  events = computed(() => {
    const items = this.$events.value() || [];
    return [...items].sort(
      (a, b) => new Date(a.startAt).getTime() - new Date(b.startAt).getTime()
    );
  });

  $event = rxResource({
    stream: ({ params }) => {
      if (!params.eventId) {
        return of(undefined);
      }

      return this.eventService.eventControllerFindByIdV1({
        id: params.eventId,
      });
    },
    params: () => ({ eventId: this.eventIdValueSignal() }),
  });

  event = computed(() => {
    const eventData = this.$event.value() as EventDto | undefined;
    return eventData;
  });

  constructor() {
    effect(() => {
      const eventId = this.queryEventId();
      if (eventId && !this.form.controls.eventId.value) {
        this.form.controls.eventId.setValue(eventId);
      }
    });
    // 개별 동의 체크박스 변경 감지 -> 전체 동의 업데이트
    effect(() => {
      const terms = this.isTermsAgreed();
      const privacy = this.isPrivacyAgreed();

      if (terms && privacy) {
        this.allAgreed.set(true);
      } else if (this.allAgreed()) {
        this.allAgreed.set(false);
      }
    });
  }

  toggleAllAgreed(checked: boolean): void {
    this.allAgreed.set(checked);
    this.isTermsAgreed.set(checked);
    this.isPrivacyAgreed.set(checked);
  }

  toggleRegion(region: Region): void {
    const current = this.form.value.interestedRegions || [];
    const index = current.indexOf(region);

    if (index > -1) {
      current.splice(index, 1);
    } else {
      current.push(region);
    }

    this.form.patchValue({ interestedRegions: [...current] });
  }

  toggleAwareness(channel: AwarenessChannel): void {
    const current = this.form.value.visitMotives || [];
    const index = current.indexOf(channel);

    if (index > -1) {
      current.splice(index, 1);
    } else {
      current.push(channel);
    }

    this.form.patchValue({ visitMotives: [...current] });
  }

  isRegionSelected(region: Region): boolean {
    return (this.form.value.interestedRegions || []).includes(region);
  }

  isAwarenessSelected(channel: AwarenessChannel): boolean {
    return (this.form.value.visitMotives || []).includes(channel);
  }

  canSubmit(): boolean {
    return this.form.valid && this.isTermsAgreed();
  }

  goBack(): void {
    this.router.navigate(['/']);
  }

  submit(): void {
    if (!this.canSubmit()) {
      if (!this.isTermsAgreed()) {
        this.toastService.error('개인정보 이용에 동의해주세요.');
      } else {
        this.toastService.error('필수 항목을 입력해주세요.');
      }
      return;
    }

    const formValue = this.form.getRawValue();
    const normalizedContact = formValue.contact.replace(/[^0-9]/g, '');

    // 동의 여부와 관계없이 입력된 값 사용 (동의하지 않은 경우 빈 배열)
    const interestedRegions = this.isPrivacyAgreed()
      ? formValue.interestedRegions
      : [];
    const visitMotives = this.isPrivacyAgreed() ? formValue.visitMotives : [];

    // 🔥 마케팅 동의 여부와 관계없이 항상 MarketingConsent 저장
    const marketingBody: CreateMarketingConsentDto = {
      contact: normalizedContact,
      name: formValue.name,
      interestedRegions: interestedRegions,
      visitMotives: visitMotives,
      isAgreed: this.isPrivacyAgreed(), // 동의 여부를 그대로 저장
      sourceType: 'PRE_REGISTRATION',
      brandType: 'STARTFAIR',
    };

    this.marketingConsentService
      .marketingConsentControllerCreateV1({ body: marketingBody })
      .subscribe({
        next: marketing => {
          this.submitPreRegistration(marketing.id);
        },
        error: err => {
          const message =
            err?.error?.message ||
            err?.message ||
            '마케팅 동의 처리 중 오류가 발생했습니다.';
          this.toastService.error({ message });
        },
      });
  }

  private submitPreRegistration(marketingId?: string): void {
    const formValue = this.form.getRawValue();

    const body: CreatePreRegistrationDto = {
      name: formValue.name,
      contact: formValue.contact.replace(/[^0-9]/g, ''),
      eventId: formValue.eventId,
      marketingId: marketingId,
      sourceType: 'STARTFAIR',
    };

    this.preRegistrationService
      .preRegistrationControllerCreateV1({ body })
      .subscribe({
        next: () => {
          const modal = this.modalService.create(RegistrationCompleteModal, {
            componentProps: {
              eventTitle: this.event()?.title || '',
              name: formValue.name,
              contact: formValue.contact,
            },
          });
          modal.closed.subscribe(() => {
            this.router.navigate(['/']);
          });
        },
        error: err => {
          const message =
            err?.error?.message ||
            err?.message ||
            '등록 중 오류가 발생했습니다.';

          this.toastService.error({ message });
        },
      });
  }

  openPolicyModal(type: 'use' | 'privacy' | 'marketing'): void {
    this.modalService.create(PolicyModal, {
      componentProps: { type },
    });
  }
}
